import CheckIcon from '@mui/icons-material/Check'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import EmailIcon from '@mui/icons-material/Email'
import ShareIcon from '@mui/icons-material/Share'
import SmsIcon from '@mui/icons-material/Sms'
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  TextField
} from '@mui/material'
import { Stack } from '@mui/system'
import { FC, useCallback, useState } from 'react'

import { CenteredLoading } from '../CenteredLoading/CenteredLoading'

interface Props {
  title: string
  link: string
  open: boolean
  handleCancel: () => void
  handleEmail: () => void
  handleSms: () => void
  regenerateLink?: (showPartsLabor: boolean) => Promise<void>
}
const COPIED_TIMEOUT = 500

export const IncludePartsStorageKey = 'INCLUDE_PARTS_BREAKDOWN'

export const ShareViaMainDialog: FC<Props> = ({
  title,
  link,
  open,
  handleCancel,
  handleEmail,
  handleSms,
  regenerateLink,
  ...props
}) => {
  const [copied, setCopied] = useState<boolean>(true)

  const copyClipboard = useCallback(() => {
    navigator.clipboard.writeText(link)
    setTimeout(() => {
      setCopied(true)
    }, COPIED_TIMEOUT)
    setCopied(false)
  }, [link])

  return (
    <Dialog
      {...props}
      open={open}
      onClose={handleCancel}
      fullWidth
      maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 'bold' }}>Share {title} Link</DialogTitle>
      <DialogContent sx={{ pb: 0 }}>
        <Stack direction={'column'} spacing={2}>
          <Button
            variant="outlined"
            onClick={handleEmail}
            fullWidth
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center',
              pl: 1
            }}>
            <Stack direction={'row'} sx={{ alignItems: 'center' }} spacing={1}>
              <Box
                sx={{
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  display: 'inline-flex',
                  p: 1
                }}>
                <EmailIcon sx={{ color: 'white' }} />
              </Box>
              <Box>Share link via Email</Box>
            </Stack>
            <ShareIcon />
          </Button>
          <Button
            variant="outlined"
            onClick={handleSms}
            fullWidth
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center',
              pl: 1
            }}>
            <Stack direction={'row'} sx={{ alignItems: 'center' }} spacing={1}>
              <Box
                sx={{
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  display: 'inline-flex',
                  p: 1
                }}>
                <SmsIcon sx={{ color: 'white' }} />
              </Box>
              <Box>Share link via SMS</Box>
            </Stack>
            <ShareIcon />
          </Button>
          {link ? (
            <Stack>
              <Stack
                direction={'row'}
                spacing={1}
                justifyContent="space-between">
                <Stack direction="column" spacing={1} sx={{ width: '20rem' }}>
                  <TextField
                    value={link}
                    variant="outlined"
                    disabled={true}
                    fullWidth
                  />
                  {regenerateLink && (
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={
                              localStorage.getItem(IncludePartsStorageKey) ===
                              'true'
                            }
                            onChange={(event) => {
                              localStorage.setItem(
                                IncludePartsStorageKey,
                                event.target.checked ? 'true' : 'false'
                              )
                              regenerateLink(event.target.checked)
                            }}
                          />
                        }
                        label="Show Parts and Labor"
                      />
                    </FormGroup>
                  )}
                </Stack>
                {copied ? (
                  <Button
                    onClick={copyClipboard}
                    variant="outlined"
                    sx={{ width: 200, height: 75 }}>
                    <ContentCopyIcon /> &nbsp; Copy Link
                  </Button>
                ) : (
                  <Button
                    onClick={copyClipboard}
                    variant="outlined"
                    color="success"
                    sx={{ width: 200, height: 75 }}>
                    <Box
                      sx={{
                        borderRadius: '50%',
                        bgcolor: 'success.main',
                        display: 'inline-flex',
                        p: 0.5,
                        mr: 1
                      }}>
                      <CheckIcon sx={{ color: 'white', fontSize: '90%' }} />
                    </Box>
                    Copied
                  </Button>
                )}
              </Stack>
              <FormHelperText>
                Copy the link to share. Anyone with this link can view this
                document.
              </FormHelperText>
            </Stack>
          ) : (
            <CenteredLoading />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {link && (
          <Button variant="text" onClick={() => window.open(link)}>
            Preview
          </Button>
        )}
        <Button variant="text" onClick={handleCancel}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}
